<template>
  <div class="form-root">
    <header class="form-header">
      <ONEButton
        :label="isEdit ? 'Back to Detail' : 'Back'"
        icon="pi pi-arrow-left"
        class="p-button-text p-button-sm"
        @click="goBack"
      />
      <h1 class="form-title">{{ isEdit ? 'Edit News / Event' : 'New News / Event' }}</h1>
    </header>

    <div class="form-card">
      <div class="form-grid">

        <!-- Title -->
        <div class="col-span-2">
          <ONEInputText
            v-model="form.Title"
            label="Title"
            :required="true"
            placeholder="Enter title"
          />
        </div>

        <!-- Type -->
        <ONEDropdown
          v-model="form.EventType"
          label="Type"
          :options="typeOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="Select type"
        />

        <!-- Category -->
        <ONEDropdown
          v-model="form.Category"
          label="Category"
          :options="categoryOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="Select category"
        />

        <!-- Event Date -->
        <ONEDatepicker
          v-model="form.EventDate"
          label="Date"
          :required="true"
          dateFormat="dd/mm/yy"
        />

        <!-- Priority -->
        <ONEDropdown
          v-model="form.Priority"
          label="Priority"
          :options="priorityOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="Select priority"
        />

        <!-- Cover Image upload -->
        <div class="col-span-2">
          <label class="upload-label">Cover Image (optional)</label>
          <ONEFileUpload
            :customUpload="true"
            accept="image/*"
            :multiple="false"
            @uploader="handleImageUpload"
          />
          <div v-if="form.ImagePath" class="upload-preview">
            <i class="pi pi-check-circle text-green-500"></i>
            <span>{{ form.ImagePath }}</span>
            <button class="clear-upload" @click="form.ImagePath = ''">
              <i class="pi pi-times"></i>
            </button>
          </div>
        </div>

        <!-- Rich HTML content -->
        <div class="col-span-2">
          <ONEEditor
            v-model="form.ContentHtml"
            label="Content (Rich Text)"
            :editorStyle="{ height: '300px' }"
          />
        </div>

        <!-- Plain description -->
        <div class="col-span-2">
          <label class="upload-label">Short Description (plain text)</label>
          <textarea
            v-model="form.Description"
            rows="3"
            class="plain-textarea"
            placeholder="Brief description shown in list views..."
          />
        </div>

        <!-- Attachment upload -->
        <div class="col-span-2">
          <label class="upload-label">Attachment (optional)</label>
          <ONEFileUpload
            :customUpload="true"
            accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx"
            :multiple="false"
            @uploader="handleAttachmentUpload"
          />
          <div v-if="form.AttachmentPath" class="upload-preview">
            <i class="pi pi-check-circle text-green-500"></i>
            <span>{{ form.AttachmentPath }}</span>
            <button class="clear-upload" @click="form.AttachmentPath = ''">
              <i class="pi pi-times"></i>
            </button>
          </div>
        </div>

      </div>

      <!-- Form actions -->
      <div class="form-actions">
        <ONEButton
          label="Cancel"
          icon="pi pi-times"
          class="p-button-outlined p-button-secondary"
          @click="goBack"
        />
        <ONEButton
          :label="isEdit ? 'Save Changes' : 'Create'"
          icon="pi pi-check"
          :loading="saving"
          @click="handleSubmit"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import moment from 'moment'
import { http } from '../../services/http'
import {
  getNewsEventById,
  createNewsEvent,
  updateNewsEvent,
  getDashboardUserContext
} from '../../services/dashboardService'

const router = useRouter()
const route = useRoute()

const id = Number(route.params.id)
const isEdit = !!id

const ctx = getDashboardUserContext()

const saving = ref(false)

const form = reactive({
  Id: 0,
  Title: '',
  Description: '',
  ContentHtml: '',
  EventType: 2,
  Category: 'General',
  EventDate: new Date(),
  Priority: 0,
  ImagePath: '',
  AttachmentPath: '',
  OrganizationId: ctx.orgId,
  BranchId: ctx.branchId,
  IsActive: true,
  CreatedBy: String(ctx.userId),
  CreatedDate: new Date(),
  VisibilityStart: new Date(),
  VisibilityEnd: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
  Type: 'News',
  Audience: 'All',
  Location: '',
  AudienceType: 0
})

const typeOptions = [
  { label: 'News', value: 2 },
  { label: 'Event', value: 1 }
]

const categoryOptions = [
  { label: 'General', value: 'General' },
  { label: 'Clinical', value: 'Clinical' },
  { label: 'HR', value: 'HR' },
  { label: 'Operations', value: 'Operations' },
  { label: 'Emergency', value: 'Emergency' },
  { label: 'Training', value: 'Training' },
  { label: 'Policy', value: 'Policy' }
]

const priorityOptions = [
  { label: 'Normal', value: 0 },
  { label: 'High', value: 1 },
  { label: 'Critical', value: 2 }
]

async function handleImageUpload(event: any) {
  const file = event.files?.[0]
  if (!file) return
  const formData = new FormData()
  formData.append('file', file)
  try {
    const res = await http.post<any>(
      '/PlatformCoreServices/Authorize/AuthorizationFileUpload/UploadFile?FolderName=NewsEventsImages',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    )
    form.ImagePath = res?.Url ?? ''
  } catch { /* upload failed — user can retry */ }
}

async function handleAttachmentUpload(event: any) {
  const file = event.files?.[0]
  if (!file) return
  const formData = new FormData()
  formData.append('file', file)
  try {
    const res = await http.post<any>(
      '/PlatformCoreServices/Authorize/AuthorizationFileUpload/UploadFile?FolderName=NewsEventsAttachments',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    )
    form.AttachmentPath = res?.Url ?? ''
  } catch { /* upload failed */ }
}

async function handleSubmit() {
  if (!form.Title?.trim()) return
  saving.value = true
  try {
    const payload = {
      ...form,
      Type: form.EventType === 1 ? 'Event' : 'News',
      EventDate: form.EventDate instanceof Date ? form.EventDate : new Date(form.EventDate),
      CreatedDate: form.CreatedDate instanceof Date ? form.CreatedDate : new Date(),
      VisibilityStart: form.VisibilityStart instanceof Date ? form.VisibilityStart : new Date(),
      VisibilityEnd: form.VisibilityEnd instanceof Date ? form.VisibilityEnd : new Date()
    }
    if (isEdit) {
      await updateNewsEvent(payload)
      router.push(`/news-events/${id}`)
    } else {
      const newId = await createNewsEvent(payload)
      router.push(newId ? `/news-events/${newId}` : '/news-events')
    }
  } finally {
    saving.value = false
  }
}

function goBack() {
  if (isEdit) router.push(`/news-events/${id}`)
  else router.push('/news-events')
}

onMounted(async () => {
  if (isEdit) {
    const item = await getNewsEventById(id)
    if (item) {
      Object.assign(form, {
        Id: item.Id,
        Title: item.Title ?? '',
        Description: item.Description ?? '',
        ContentHtml: item.ContentHtml ?? '',
        EventType: item.EventType ?? 2,
        Category: item.Category ?? 'General',
        EventDate: item.EventDate ? new Date(item.EventDate) : new Date(),
        Priority: item.Priority ?? 0,
        ImagePath: item.ImagePath ?? '',
        AttachmentPath: item.AttachmentPath ?? '',
        Type: item.Type ?? 'News',
        Audience: item.Audience ?? 'All',
        Location: item.Location ?? '',
        AudienceType: item.AudienceType ?? 0
      })
    }
  }
})
</script>

<style scoped>
.form-root { min-height: 100vh; background: var(--one-rule-soft); padding: 1.5rem; font-family: 'Inter', sans-serif; }
.form-header { margin-bottom: 1rem; }
.form-title { font-size: 1.4rem; font-weight: 700; color: #0f172a; margin: 0.5rem 0 0 0; }
.form-card { max-width: 860px; margin: 0 auto; background: #fff; border: 1px solid var(--one-rule); border-radius: 12px; padding: 2rem; box-shadow: 0 1px 4px rgba(0,0,0,0.04); }
.form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem 1.5rem; margin-bottom: 1.5rem; }
.col-span-2 { grid-column: span 2; }
.upload-label { display: block; font-size: 0.75rem; font-weight: 600; color: var(--one-muted); text-transform: uppercase; letter-spacing: 0.04em; margin-bottom: 0.5rem; }
.upload-preview { display: flex; align-items: center; gap: 6px; margin-top: 0.5rem; font-size: 0.82rem; color: #475569; }
.clear-upload { background: none; border: none; color: #94a3b8; cursor: pointer; padding: 2px; }
.clear-upload:hover { color: #ef4444; }
.plain-textarea { width: 100%; border: 1px solid var(--one-rule); border-radius: 8px; padding: 8px 12px; font-size: 0.875rem; color: #1e293b; background: #f8fafc; outline: none; resize: vertical; transition: border-color 0.15s; font-family: inherit; }
.plain-textarea:focus { border-color: #5eead4; background: #fff; }
.form-actions { display: flex; justify-content: flex-end; gap: 0.75rem; border-top: 1px solid var(--one-rule-soft); padding-top: 1.25rem; margin-top: 0.5rem; }
</style>
